<template>
  <tr class="usage-terms-table-item">
    <td v-if="!isVivoProvider" class="usage-terms-table-item-icon">
      <img :src="wifeedTermsUrl" />
    </td>

    <td>
      <app-toggle v-model="isActive"
                  @input="showConfirmationDialog"></app-toggle>
    </td>

    <td>{{ statusText }}</td>

    <td>{{ item.name }}</td>

    <td>{{ lastChangeText }}</td>

    <td>{{ publicationDateText }}</td>

    <td class="usage-terms-table-item-actions">
      <i v-if="!isPublished && isDraft"
         class="mdi mdi-square-edit-outline pointer"
         v-tooltip="$t('message.generics.message.edit')"
         @click.stop="goToEditPage"></i>
      <i class="mdi mdi-content-copy pointer"
         v-tooltip="$t('message.generics.message.duplicate')"
         @click="copyTerm"></i>
      <i class="mdi mdi-eye pointer"
         v-tooltip="$t('message.generics.message.view')"
         @click="openPreview">
      </i>
      <i v-if="isDraft"
         class="mdi mdi-delete pointer"
         v-tooltip="$t('message.generics.message.delete')"
         @click.stop="deleteTerm"></i>

      <usage-terms-preview ref="preview"
                           :term="item" />
    </td>

    <app-confirmation-modal v-model="pendingPublishChange"
                            :maxWidth="500"
                            :confirm-label="$t('message.generics.message.confirm')"
                            @confirm="onPendingPublishChangeConfirm"
                            @cancel="cancelPublishChange">
      <span slot="heading">
        {{ publishChangeText }}
      </span>
      <span>
        {{ publishChangeConfirmText }}
      </span>
    </app-confirmation-modal>

    <app-confirmation-modal v-model="showUseWifeedConfirmModal"
                            :maxWidth="500"
                            :persistent="false"
                            :confirm-label="$t('message.generics.message.yes')"
                            :cancel-label="$t('message.generics.message.no')"
                            @confirm="setUseWifeedFlagAndChangeStatus(true)"
                            @cancel="setUseWifeedFlagAndChangeStatus(false)"
                            @closed="cancelPublishChange">
      <span slot="heading">
        {{$t('message.termsOfUse.message.keepWifeedTerms')}}
      </span>
      <span>
        {{$t('message.termsOfUse.message.keepWifeedTermsConfirmation')}}
      </span>
    </app-confirmation-modal>

  </tr>
</template>

<script>
import AppConfirmationModal from "@components/ConfirmationModal";
import UsageTermsPreview from "@views/usage-terms/components/UsageTermsPreview";
import AppToggle from "@components/Toggle";

export default {
  components: {
    AppToggle,
    UsageTermsPreview,
    AppConfirmationModal,
  },

  data() {
    return {
      isActive: false,
      useWifeed: false,
      pendingPublishChange: false,
      showUseWifeedConfirmModal: false,
    };
  },

  props: {
    item: {
      type: Object,
    },
    isVivoProvider: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    statusText() {
      return {
        ["PUBLISHED"]: this.$t('message.termsOfUse.message.published'),
        ["DRAFT"]: this.$t('message.generics.message.draft'),
        ["DISABLED"]: this.$t('message.termsOfUse.message.disabled'),
      }[this.item.status];
    },
    publicationDateText() {
      return this.item.publicationDate
        ? this.$date.format(new Date(this.item.publicationDate))
        : "---";
    },
    lastChangeText() {
      return this.item.lastChange
        ? this.$date.formatDateAndHour(new Date(this.item.lastChange))
        : "---";
    },
    isPublished() {
      return this.item.status === "PUBLISHED";
    },
    isDraft() {
      return this.item.status === "DRAFT";
    },
    permissions() {
      return this.$store.state.users.user.permissions;
    },
    canDeactivateWifeedTerms() {
      return this.$store.state.users.user.tipo === "ESTABELECIMENTO" ? this.permissions.includes("DISABLE_PLATFORM_USE_TERMS") : false;
    },
    publishChangeText() {
      return this.isPublished
        ? this.$t('message.termsOfUse.message.unpublishTermsOfUse')
        : this.$t('message.termsOfUse.message.publishTermsOfUse');
    },
    publishChangeConfirmText() {
      return this.isPublished
        ? this.$t('message.termsOfUse.message.unpublishTermsOfUseConfirmation')
        : this.$t('message.termsOfUse.message.publishTermsOfUseConfirmation');
    },
    isAdmin() {
      return this.$store.state.users.admin && !this.$store.state.users.user;
    },
    user() {
      return this.$store.state.users.user;
    },
    userId() {
      return this.isAdmin ? 0 : this.user.id;
    },
    isWifeedTermsActive() {
      return this.item.useWifeedTerms;
    },
    wifeedTermsUrl() {
      return this.isWifeedTermsActive
        ? "/images/logo/logo.png"
        : "/images/logo/logo-off.png";
    },    
  },

  methods: {
    goToEditPage() {
      const editPath = this.isAdmin
        ? `/admin/termos-de-uso/editar/${this.item.id}`
        : `/termos-de-uso/editar/${this.item.id}`;

      this.$router.push({ path: editPath, userId: this.userId });
    },
    async copyTerm() {
      try {
        await this.$api.usageTerms.copy({
          usageTermId: this.item.id,
          userId: this.userId,
        });

        this.$emit("copied");
      } catch (error) {
        console.log(error);
      }
    },
    async deleteTerm() {
      try {
        await this.$api.usageTerms.remove({
          usageTermId: this.item.id,
          userId: this.userId,
        });

        this.$emit("removed");
      } catch (error) {
        console.log(error);
      }
    },
    openPreview() {
      this.$refs.preview.open();
    },
    cancelPublishChange() {
      this.useWifeed = false;
      this.isActive = !this.isActive;
      this.pendingPublishChange = false;
      this.showUseWifeedConfirmModal = false;
    },
    showConfirmationDialog() {
      this.pendingPublishChange = true;
    },
    async changeUsageTermStatus({ useWifeedTerms } = {}) {
      try {
        if (this.isPublished) {
          await this.$api.usageTerms.remove({
            usageTermId: this.item.id,
            userId: this.userId,
          });
        } else {
          await this.$api.usageTerms.publish({
            usageTermId: this.item.id,
            useWifeed: useWifeedTerms || this.useWifeed,
            userId: this.userId,
          });
        }

        this.useWifeed = false;

        this.$emit("statusChanged");
      } catch (error) {
        this.isActive = !this.isActive;
      }
    },
    onPendingPublishChangeConfirm() {
      if (this.isPublished) {
        this.changeUsageTermStatus();
      } else {
        if (this.isDraft) {
          if ( !this.isAdmin && this.canDeactivateWifeedTerms) {
            this.showUseWifeedConfirmModal = true;
          } else {
            this.changeUsageTermStatus();
          }
        } else {
          const useWifeedTerms = this.item.useWifeedTerms;

          this.changeUsageTermStatus({
            useWifeedTerms,
          });
        }
      }

      this.pendingPublishChange = false;
    },
    async setUseWifeedFlagAndChangeStatus(useWifeed) {
      this.useWifeed = useWifeed;
      await this.changeUsageTermStatus();
      this.showUseWifeedConfirmModal = false;
    },
  },

  mounted() {
    this.isActive = this.isPublished;
  },

  beforeDestroy() {},
};
</script>

<style lang="scss" scoped>
.usage-terms-table-item {
  .usage-terms-table-item-icon {
    img {
      width: 30px;
    }
  }

  .usage-terms-table-item-actions {
    i {
      margin-right: 5px;
    }
  }
}
</style>
